import React from 'react';
import { Home, Brain, Calendar, Users } from 'lucide-react';
import { SidebarMenuItem } from './DashboardSidebar';

const iconSize = 20;
const iconStroke = 2;

/** Meal plan section path segment and short label for sidebar (fits one line). Exported for breadcrumb alignment. */
export const MEAL_PLAN_SECTIONS: { section: string; label: string }[] = [
  { section: 'daily-nutrition', label: 'Daily Nutrition' },
  { section: 'nutrition-profile', label: 'Nutrition Profile' },
  { section: 'plan-targets', label: 'Plan Targets' },
  { section: 'daily-meals', label: 'Meal Plans' },
  { section: 'qa', label: "Agent's Q&A" },
  { section: 'monitoring', label: 'Monitoring' },
];

/** Section path -> display label (same as sidebar). Use for breadcrumb so it matches left nav. */
export const SECTION_LABELS: Record<string, string> = Object.fromEntries(
  MEAL_PLAN_SECTIONS.map((s) => [s.section, s.label]),
);

/** Meal Plans sub-step and tab for left-pane navigation (URL ?step= & ?tab=). */
export type MealPlanStep = 'settings' | 'view' | 'draft';
export type MealPlanTab = 'nutrient_comparison' | 'daily_meal_plan' | 'chat';

export interface SidebarContext {
  patientId?: string;
  patientName?: string | null;
  goalId?: string;
  /** Display name of the current diet plan (e.g. "muscle gain") for sidebar; falls back to "Diet Plan" if not set. */
  mealPlanName?: string | null;
  mealPlanSection?: string;
  mealPlanStep?: MealPlanStep;
  mealPlanTab?: MealPlanTab;
  /** Optional sidebar items under the patient (e.g. Appointments, Diet Plans, Nutrition Profile, Insights) when not on a specific diet plan. */
  patientExtraChildren?: SidebarMenuItem[];
  /** Reserved for diet plan context (e.g. requirements chat session). */
  requirementsChatSessionId?: number | null;
}

/**
 * Creates sidebar menu items with navigation handlers.
 * When context is provided (e.g. on meal plan detail), Patients gets a tree: Patient name → Meal plan sections.
 * @param navigate - Navigation function from react-router-dom
 * @param currentPath - Current pathname to determine active state
 * @param context - Optional context for patient/meal plan pages to show tree under Patients
 */
export const createSidebarItems = (
  navigate: (path: string) => void,
  currentPath: string,
  context?: SidebarContext
): SidebarMenuItem[] => {
  const patientHref = context?.patientId ? `/patients/${context.patientId}` : '';
  const isOnDietPlanPage = context?.goalId && currentPath.includes(`/diet-plan/${context.goalId}`);
  const isOnPatientPage = context?.patientId && (currentPath === `/patients/${context.patientId}` || currentPath.startsWith(`/patients/${context.patientId}/`));
  const isOnMealPlan = context?.goalId && (isOnDietPlanPage || (isOnPatientPage && !currentPath.includes('/diet-plan/')));
  const isOnPatientDetail = patientHref && currentPath === patientHref;

  const patientsItem: SidebarMenuItem = {
    id: 'patients',
    label: 'Patients',
    icon: '👥',
    iconElement: <Users size={iconSize} strokeWidth={iconStroke} />,
    onClick: () => navigate('/patients'),
    isActive: currentPath === '/patients',
  };

  if (context?.patientId && context?.patientName) {
    const baseMealPlansPath = context.goalId
      ? `/patients/${context.patientId}/diet-plan/${context.goalId}/daily-meals`
      : '';

    const mealPlanSectionItems: SidebarMenuItem[] = (context.goalId && isOnMealPlan)
      ? [
          ...MEAL_PLAN_SECTIONS.flatMap(({ section, label }) => {
            if (section === 'daily-meals') {
              return [{
                id: 'meal-plan-daily-meals',
                label: 'Meal Plans',
                icon: '',
                onClick: () => navigate(`${baseMealPlansPath}?reset=true`),
                isActive: context.mealPlanSection === section,
              }];
            }
            return [{
              id: `meal-plan-${section}`,
              label,
              icon: '',
              onClick: () => {
                navigate(`/patients/${context.patientId}/diet-plan/${context.goalId}/${section}`);
              },
              isActive: context.mealPlanSection === section,
            }];
          }),
        ]
      : [];
    const dietPlanLabel = (context.mealPlanName && context.mealPlanName.trim()) ? context.mealPlanName.trim() : 'Diet Plan';
    const dietPlanParentItem: SidebarMenuItem | null = (context.goalId && mealPlanSectionItems.length > 0)
      ? {
          id: 'diet-plan-parent',
          label: dietPlanLabel,
          icon: '',
          onClick: () => navigate(`/patients/${context.patientId}/diet-plan/${context.goalId}/daily-nutrition`),
          isActive: Boolean(isOnDietPlanPage),
          children: mealPlanSectionItems,
        }
      : null;

    /** Diet Plans: when inside a plan, show as parent with current Diet Plan (and its sections) as child; otherwise a link to the list. */
    const dietPlansItem: SidebarMenuItem = dietPlanParentItem
      ? {
          id: 'patient-diet-plans',
          label: 'Diet Plans',
          icon: '',
          onClick: () => navigate(`/patients/${context.patientId}/diet-plans`),
          isActive: currentPath === `/patients/${context.patientId}/diet-plans` || Boolean(isOnDietPlanPage),
          children: [dietPlanParentItem],
        }
      : {
          id: 'patient-diet-plans',
          label: 'Diet Plans',
          icon: '',
          onClick: () => navigate(`/patients/${context.patientId}/diet-plans`),
          isActive: currentPath === `/patients/${context.patientId}/diet-plans`,
        };

    /** Under patient: Appointments, Diet Plans (with optional nested Diet Plan), Nutrition Profile, Insights. */
    const patientChildItems: SidebarMenuItem[] = [
      { id: 'patient-appointments', label: 'Appointments', icon: '', onClick: () => navigate(`/patients/${context.patientId}/appointments`), isActive: currentPath === `/patients/${context.patientId}/appointments` },
      dietPlansItem,
      { id: 'patient-nutrition-profile', label: 'Nutrition Profile', icon: '', onClick: () => navigate(`/patients/${context.patientId}/nutrition-profile`), isActive: currentPath === `/patients/${context.patientId}/nutrition-profile` },
      { id: 'patient-insights', label: 'Insights', icon: '', onClick: () => navigate(`/patients/${context.patientId}/insights`), isActive: currentPath === `/patients/${context.patientId}/insights` },
    ];
    patientsItem.children = [
      {
        id: `patient-${context.patientId}`,
        label: context.patientName,
        icon: '',
        onClick: () => navigate(context.goalId ? `/patients/${context.patientId}/diet-plans` : patientHref + '?tab=appointments'),
        isActive: Boolean(isOnPatientDetail),
        children: patientChildItems,
      },
    ];
  }

  return [
    {
      id: 'dashboard',
      label: 'Home',
      icon: '🏠',
      iconElement: <Home size={iconSize} strokeWidth={iconStroke} />,
      onClick: () => navigate('/dashboard'),
      isActive: currentPath === '/dashboard',
    },
    {
      id: 'ai-requests',
      label: 'AI Requests',
      icon: '🧠',
      iconElement: <Brain size={iconSize} strokeWidth={iconStroke} />,
      onClick: () => navigate('/ai-requests'),
      isActive: currentPath === '/ai-requests' || currentPath.startsWith('/ai-requests/'),
    },
    {
      id: 'appointments',
      label: 'Appointments',
      icon: '📅',
      iconElement: <Calendar size={iconSize} strokeWidth={iconStroke} />,
      onClick: () => navigate('/appointments'),
      isActive: currentPath === '/appointments' || currentPath.startsWith('/appointments/'),
    },
    patientsItem,
  ];
};
